import React from "react";
import { useSelector } from "react-redux";
import { NMStatsTable, BarChart } from "@neumetric/blueprint-lib/components";
import { getDepartments } from "@neumetric/blueprint-lib/store";
import { getMitigatorDashboardStatsData } from "../../../store";
import { ExportSection } from "../ExportSection";
import ExportAxisLabel from "../ExportAxisLabel";

const StatusColumns = [
  {
    typeRepresentation: "TITLE",
    title: "",
    attribute: "department",
    width: "14%",
  },
  {
    title: "OVERALL",
    id: "Overall",
    attribute: "total",
    width: "11%",
    highlight: { header: true, cell: true },
    typeRepresentation: "DOT",
    overallFlag: true,
    align: "center-right",
  },
  {
    title: "Pending",
    id: "pending",
    attribute: "pending",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Accepted",
    id: "closed",
    attribute: "accepted",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Partial",
    id: "started",
    attribute: "partial",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Mitigated",
    id: "complete",
    attribute: "mitigated",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Tolerated",
    id: "urgent",
    attribute: "tolerated",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Redundant",
    id: "redundant",
    attribute: "redundant",
    typeRepresentation: "DOT",
    align: "center-right",
    width: "13%",
  },
];

const parseKey = (k) => {
  if (typeof k !== "string") return { department: "", value: null };
  const safe = k.replace(/\bnil\b/g, "null");
  try {
    const arr = JSON.parse(safe);
    return { department: arr?.[0], value: arr?.[1] };
  } catch {
    return { department: "", value: null };
  }
};

const emptyRow = (department) => ({
  department,
  total: 0,
  pending: 0,
  accepted: 0,
  partial: 0,
  mitigated: 0,
  medium: 0,
  tolerated: 0,
  redundant: 0,
  other: 0,
});

const statusToAttr = (status) => {
  if (status == null || status === "") return "other";
  const s = status.toString().trim().toLowerCase();

  const MAP = {
    pending: "pending",
    accepted: "accepted",
    partial: "partial",
    mitigated: "mitigated",
    tolerated: "tolerated",
    redundant: "redundant",
    medium: "medium",

    open: "pending",
    closed: "accepted",
    started: "partial",
    complete: "mitigated",
    urgent: "tolerated",
  };

  return MAP[s] || "other";
};

const SERIES_ORDER = [
  "Redundant",
  "Tolerated",
  "Mitigated",
  "Partial",
  "Accepted",
  "Pending",
];

const VALUE_KEY = {
  Pending: "pending",
  Accepted: "accepted",
  Partial: "partial",
  Mitigated: "mitigated",
  Tolerated: "tolerated",
  Redundant: "redundant",
};

const COLOR_KEY = {
  Pending: "pending",
  Accepted: "closed",
  Partial: "started",
  Mitigated: "complete",
  Tolerated: "urgent",
  Redundant: "redundant",
};

export const DepartmentVsStatus = ({ user }) => {
  const mitigatorDashboardData = useSelector(getMitigatorDashboardStatsData);
  const departments = useSelector(getDepartments);
  const stats = mitigatorDashboardData?.department_vs_status || {};

  const companyLabel = user?.org?.brandname ?? "Luciance";

  const dashZero = (v) => {
    const n = Number(v) || 0;
    return n === 0 ? "-" : n;
  };

  const toTableRow = (r) => ({
    ...r,
    total: dashZero(r.total),
    pending: dashZero(r.pending),
    accepted: dashZero(r.accepted),
    partial: dashZero(r.partial),
    mitigated: dashZero(r.mitigated),
    tolerated: dashZero(r.tolerated),
    redundant: dashZero(r.redundant),
  });

  const { tableRows, datasets } = React.useMemo(() => {
    const rowsByDept = {};
    (departments || []).forEach((d) => (rowsByDept[d.id] = emptyRow(d.id)));

    Object.entries(stats).forEach(([k, count]) => {
      const { department, value: status } = parseKey(k);
      if (!department) return;

      if (!rowsByDept[department])
        rowsByDept[department] = emptyRow(department);

      const attr = statusToAttr(status);
      const n = Number(count) || 0;

      const targetKey =
        rowsByDept[department][attr] === undefined ? "other" : attr;

      rowsByDept[department][targetKey] += n;
      rowsByDept[department].total += n;
    });

    const deptList = departments?.length
      ? departments.map((d) => d.id)
      : Object.keys(rowsByDept);

    const numericRows = deptList.map((dep) => rowsByDept[dep] || emptyRow(dep));

    const totalNumeric = numericRows.reduce(
      (acc, r) => ({
        ...acc,
        total: acc.total + (Number(r.total) || 0),
        pending: acc.pending + (Number(r.pending) || 0),
        accepted: acc.accepted + (Number(r.accepted) || 0),
        partial: acc.partial + (Number(r.partial) || 0),
        mitigated: acc.mitigated + (Number(r.mitigated) || 0),
        tolerated: acc.tolerated + (Number(r.tolerated) || 0),
        redundant: acc.redundant + (Number(r.redundant) || 0),
        other: acc.other + (Number(r.other) || 0),
      }),
      {
        ...emptyRow(companyLabel),
        department: companyLabel,
      },
    );

    const hasAnyData = (Number(totalNumeric.total) || 0) > 0;

    const tableRows = hasAnyData
      ? [
          {
            ...toTableRow(totalNumeric),
            department: companyLabel,
            highlight: true,
          },
          ...numericRows.map(toTableRow),
        ]
      : [];

    const datasets = {};
    deptList.forEach((dep) => {
      const r = rowsByDept[dep] || emptyRow(dep);
      datasets[dep] = SERIES_ORDER.map((label) => {
        const valueAttr = VALUE_KEY[label];
        const colorKey = COLOR_KEY[label];
        return {
          key: colorKey,
          label,
          value: Number(r[valueAttr] || 0),
        };
      });
    });

    return { tableRows, datasets };
  }, [departments, stats, companyLabel]);

  const csvStatusColumns = React.useMemo(() => {
    if (!Array.isArray(StatusColumns) || StatusColumns.length === 0) return [];

    return StatusColumns.map((col, idx) => {
      if (idx !== 0) return col;

      return {
        ...col,
        title: "Risks (Department vs Status)", // CSV header only
      };
    });
  }, [StatusColumns]);

  return (
    <div className="d-flex gap-20 p20">
      <ExportSection
        index={1}
        moduleKey={"Mitigator_Department_vs_Status_Status"}
        exportType="table"
        exportData={{
          title: "Risks - Department vs Status",
          columns: csvStatusColumns,
          rows: tableRows,
        }}
        style={{ flex: "1 1 55%", maxWidth: "60%" }}
      >
        <NMStatsTable
          title="Risks"
          subTitle="Department vs Status"
          columns={StatusColumns}
          dataSource={tableRows}
        />
      </ExportSection>
      <ExportSection
        index={2}
        moduleKey="Mitigator_Department_vs_Status_Status"
        style={{ flex: "1 1 45%", maxWidth: "50%", position: "relative" }}
      >
        <ExportAxisLabel text="Department" />

        <BarChart
          title="Risks"
          subtitle="Department vs Status"
          label={{ xAxis: "Risks", yAxis: "" }}
          datasets={datasets}
          stackType="normal"
          classes={{ root: "h-575-px mt-12" }}
        />
      </ExportSection>
    </div>
  );
};
